import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as PendingIcon,
  SkipNext as SkipIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const ProductNavigationDialog = ({ 
  open, 
  onClose, 
  items = [], 
  currentIndex, 
  onSelectItem 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0); // 0: All, 1: Pending, 2: Counted, 3: Skipped

  // Filter and search items
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Apply status filter
    switch (filterTab) {
      case 1: // Pending
        filtered = items.filter(item => !item.is_counted && !item.is_skipped);
        break;
      case 2: // Counted
        filtered = items.filter(item => item.is_counted);
        break;
      case 3: // Skipped
        filtered = items.filter(item => item.is_skipped);
        break;
      default: // All
        break;
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const productName = item.product?.name?.toLowerCase() || '';
        const productCode = item.product?.code?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return productName.includes(search) || productCode.includes(search);
      });
    }

    return filtered.map((item, originalIndex) => ({
      ...item,
      originalIndex: items.indexOf(item)
    }));
  }, [items, searchTerm, filterTab]);

  const getStatusIcon = (item) => {
    if (item.is_skipped) return <SkipIcon color="warning" />;
    if (item.is_counted) {
      const hasVariance = item.calculateVariance && Math.abs(item.calculateVariance()) > 0;
      return hasVariance ? <WarningIcon color="warning" /> : <DoneIcon color="success" />;
    }
    return <PendingIcon color="disabled" />;
  };

  const getStatusChip = (item) => {
    if (item.is_skipped) return <Chip label="Bỏ qua" color="warning" size="small" />;
    if (item.is_counted) {
      const variance = item.calculateVariance ? item.calculateVariance() : 0;
      if (Math.abs(variance) > 0) {
        return <Chip 
          label={`${variance >= 0 ? '+' : ''}${variance.toFixed(2)}`} 
          color="warning" 
          size="small" 
        />;
      }
      return <Chip label="Chính xác" color="success" size="small" />;
    }
    return <Chip label="Chưa kiểm" color="default" size="small" />;
  };

  const handleSelectItem = (item) => {
    onSelectItem(item.originalIndex);
    onClose();
  };

  const tabCounts = {
    all: items.length,
    pending: items.filter(item => !item.is_counted && !item.is_skipped).length,
    counted: items.filter(item => item.is_counted).length,
    skipped: items.filter(item => item.is_skipped).length
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Danh sách sản phẩm</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Tìm kiếm sản phẩm..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Filter Tabs */}
        <Tabs 
          value={filterTab} 
          onChange={(e, newValue) => setFilterTab(newValue)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Tất cả (${tabCounts.all})`} />
          <Tab label={`Chưa kiểm (${tabCounts.pending})`} />
          <Tab label={`Đã kiểm (${tabCounts.counted})`} />
          <Tab label={`Bỏ qua (${tabCounts.skipped})`} />
        </Tabs>

        {/* Product List */}
        <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
          {filteredItems.map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleSelectItem(item)}
                selected={item.originalIndex === currentIndex}
              >
                <ListItemIcon>
                  {getStatusIcon(item)}
                </ListItemIcon>
                <ListItemText
                  primary={item.getProductDisplayName ? item.getProductDisplayName() : item.product?.name}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Dự kiến: {parseFloat(item.expected_quantity || 0).toFixed(2)}
                        {item.is_counted && ` • Thực tế: ${parseFloat(item.physical_count || 0).toFixed(2)}`}
                      </Typography>
                      {item.notes && (
                        <Typography variant="caption" color="text.secondary">
                          Ghi chú: {item.notes}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ ml: 1 }}>
                  {getStatusChip(item)}
                  <Typography variant="caption" display="block" color="text.secondary">
                    #{item.originalIndex + 1}
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
          
          {filteredItems.length === 0 && (
            <ListItem>
              <ListItemText
                primary="Không tìm thấy sản phẩm"
                secondary="Thử thay đổi từ khóa tìm kiếm hoặc bộ lọc"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default ProductNavigationDialog;